import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Paper,
  Avatar,
  Grid,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Download,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Inventory2,
  Shield,
  FileUpload,
  WarningAmber,
  TableRows,
  Description,
  Close,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { fetchProductosFinancieros } from '../../../utils/generalsFetch';
import { camposGarantia } from '../../../utils/staticvariables';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';

/* ─── Constants ────────────────────────────────────────────────────────────── */
const FRECUENCIAS = ['diario', 'semanal', 'quincenal', 'mensual'];
const METODOS = ['fija', 'insoluto', 'plazo_indefinido'];
const CAMPO_MONTO = 10;
const CAMPO_PLAZO = 11;
const CAMPO_FRECUENCIA = 12;

const FIXED_EXTRA_COLUMNS = [
  { key: 'metodo',            label: 'Metodo',            required: true  },
  { key: 'fecha_solicitud',   label: 'Fecha Solicitud',   required: false },
  { key: 'fecha_inicio',      label: 'Fecha Inicio',      required: false },
  { key: 'monto_inicial',     label: 'Monto Inicial',     required: false },
  { key: 'balance_pendiente', label: 'Balance Pendiente', required: false },
  { key: 'atraso',            label: 'Atraso',            required: false },
];

const FALLBACK_COLUMNS = [
  { key: 'identificacion', label: 'Identificacion',  required: true  },
  { key: 'nombre',         label: 'Nombre',          required: true  },
  { key: 'celular',        label: 'Celular',          required: true  },
  { key: 'direccion',      label: 'Direccion',        required: false },
  { key: 'monto_solicitado',label:'Monto Solicitado', required: true  },
  { key: 'plazo',          label: 'Plazo',            required: true  },
  { key: 'frecuencia',     label: 'Frecuencia',       required: true  },
  { key: 'interes',        label: 'Interes',          required: true  },
  { key: 'mora_porcentaje',label: 'Mora Porcentaje',  required: true  },
  ...FIXED_EXTRA_COLUMNS,
];

const INV_PREFIX   = 'inv__';
const CAMPO_PREFIX = 'c__';
const GAR_PREFIX   = 'gar__';

function campoKey(id) { return `${CAMPO_PREFIX}${id}`; }
function invKey(id)   { return `${INV_PREFIX}${id}`;   }
function garKey(id)   { return `${GAR_PREFIX}${id}`;   }

/* ─── Pure helpers (unchanged logic) ──────────────────────────────────────── */
function buildGarantiaCols(allowedCategories) {
  if (!allowedCategories?.length) return [];
  const cols = [{ key: garKey('categoria'), label: 'Garantia Categoria', required: true, _isGarantia: true, _isCategoria: true, _hint: allowedCategories.join('|') }];
  const seenIds = new Set();
  allowedCategories.forEach(catValue => {
    const catData = camposGarantia.find(c => c.value === catValue);
    if (!catData) return;
    catData.campos.forEach(campo => {
      if (!seenIds.has(campo.id)) { seenIds.add(campo.id); cols.push({ key: garKey(campo.id), label: `Garantia - ${campo.name}`, required: false, _campoId: campo.id, _type: campo.type, _isGarantia: true }); }
    });
  });
  return cols;
}

function buildActiveColumns(formularioCampos, camposInventario, garantiaCols) {
  if (!formularioCampos?.length) return FALLBACK_COLUMNS;
  const formularioCols = formularioCampos.map(c => ({ key: campoKey(c.id), label: c.alias && c.alias !== '' ? c.alias : c.name, required: !!c.required, _campoId: c.id, _type: c.type, _isFormulario: true }));
  const invCols = (camposInventario || []).map(c => ({ key: invKey(c.id), label: c.alias && c.alias !== '' ? c.alias : c.name, required: !!c.required, _campoId: c.id, _type: c.type, _isInventory: true }));
  return [...formularioCols, ...FIXED_EXTRA_COLUMNS, ...invCols, ...(garantiaCols || [])];
}

function exampleValueForCampo(campoId, type) {
  const d = { 1: 'Juan Pérez', 2: '001-1234567-8', 3: '1990-01-15', 4: 'Soltero', 5: '809-555-0001', 6: 'Calle Falsa 123', 7: 'Empleado', 8: 'Empresa ABC', 9: 5000, 10: 50000, 11: 12, 12: 'mensual', 13: 5, 14: 2, 1753886767728: '809-555-0000' };
  if (d[campoId] !== undefined) return d[campoId];
  if (type === 'monto' || type === 'numero') return 0;
  if (type === 'fecha') return '2026-01-01';
  return '';
}

function buildExampleRow(activeCols) {
  return activeCols.map(col => {
    if (col._isFormulario) return exampleValueForCampo(col._campoId, col._type);
    if (col._isInventory) return col._type === 'monto' || col._type === 'numero' ? 0 : '';
    if (col._isGarantia) { if (col._isCategoria) return col._hint?.split('|')[0] || ''; return col._type === 'monto' || col._type === 'numero' ? 0 : ''; }
    if (col.key === 'metodo') return 'fijo';
    if (col.key === 'fecha_solicitud') return '2026-01-01';
    if (col.key === 'fecha_inicio') return '2026-01-15';
    if (col.key === 'monto_inicial') return 0;
    if (col.key === 'balance_pendiente') return 0;
    if (col.key === 'atraso') return 0;
    return '';
  });
}

function downloadTemplate(activeCols) {
  const ws = XLSX.utils.aoa_to_sheet([activeCols.map(c => c.label), buildExampleRow(activeCols)]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Prestamos');
  XLSX.writeFile(wb, 'plantilla_importar_prestamos.xlsx');
}

function validateRow(row, activeCols) {
  const errors = [];
  const categoriaGarantia = String(row[garKey('categoria')] || '').toLowerCase();
  const garantiaCatData = camposGarantia.find(c => c.value === categoriaGarantia);
  activeCols.forEach(col => {
    const val = row[col.key];
    if (col._isFormulario) {
      if (!col.required) return;
      const id = col._campoId;
      if (id === CAMPO_MONTO)      { if (!val || isNaN(Number(val)) || Number(val) <= 0) errors.push('Monto solicitado inválido'); }
      else if (id === CAMPO_PLAZO) { if (!val || isNaN(Number(val)) || Number(val) <= 0) errors.push('Plazo inválido'); }
      else if (id === CAMPO_FRECUENCIA) { if (!FRECUENCIAS.includes(String(val || '').toLowerCase())) errors.push(`Frecuencia inválida (${FRECUENCIAS.join('|')})`); }
      else { if (val === '' || val === undefined || val === null) errors.push(`${col.label} es requerido`); }
    } else if (col._isInventory) {
      if (!col.required) return;
      if (val === '' || val === undefined || val === null) errors.push(`${col.label} es requerido`);
    } else if (col._isGarantia) {
      if (col._isCategoria) { const allowed = col._hint?.split('|') || []; if (!allowed.includes(categoriaGarantia)) errors.push(`Categoría de garantía inválida (${col._hint})`); }
      else { const req = garantiaCatData?.campos.find(c => c.id === col._campoId)?.required; if (req && (val === '' || val === undefined || val === null)) errors.push(`${col.label} es requerido`); }
    } else {
      if (col.key === 'metodo') { if (!METODOS.includes(String(val || '').toLowerCase())) errors.push(`Método inválido (${METODOS.join('|')})`); }
    }
  });
  return errors;
}

function coerceValue(val, type) {
  if (type === 'monto' || type === 'numero') return Number(val) || 0;
  return String(val || '');
}

const STATUS = { pending: 'pending', ok: 'ok', error: 'error', importing: 'importing' };

/* ─── Design tokens ────────────────────────────────────────────────────────── */
const GREEN       = '#2E7D32';
const GREEN_LIGHT = alpha('#2E7D32', 0.08);
const GREEN_MID   = alpha('#2E7D32', 0.14);

/* ─── Amortización preview helpers ─────────────────────────────────────────── */
function addFreqPeriod(date, freq) {
  const d = new Date(date.getTime());
  if      (freq === 'mensual')    d.setMonth(d.getMonth() + 1);
  else if (freq === 'quincenal')  d.setDate(d.getDate() + 15);
  else if (freq === 'semanal')    d.setDate(d.getDate() + 7);
  else if (freq === 'diario')     d.setDate(d.getDate() + 1);
  return d;
}

function extractLoanParams(row) {
  const fGet = (campoId, fallback) => {
    const v = row[campoKey(campoId)];
    return v !== undefined && v !== '' ? v : row[fallback];
  };
  return {
    capital:           Number(fGet(CAMPO_MONTO, 'monto_solicitado')) || 0,
    plazo:             Number(fGet(CAMPO_PLAZO,  'plazo'))           || 0,
    frecuencia:        String(fGet(CAMPO_FRECUENCIA, 'frecuencia') || 'mensual').toLowerCase(),
    interes:           Number(fGet(13, 'interes'))                   || 0,
    metodo:            String(row.metodo || 'fija').toLowerCase(),
    balance_pendiente: Number(row.balance_pendiente)                 || 0,
    fecha_inicio:      row.fecha_inicio || new Date().toISOString().split('T')[0],
  };
}

function calcAmortizacionPreview({ capital, plazo, frecuencia, interes, metodo, balance_pendiente, fecha_inicio }) {
  if (!capital || !plazo || plazo <= 0) return [];
  const n    = Math.max(1, Math.round(plazo));
  const tasa = interes / 100;
  const freq = String(frecuencia).toLowerCase();

  let r;
  if      (freq === 'mensual')    r = tasa;
  else if (freq === 'quincenal')  r = tasa / 2;
  else if (freq === 'semanal')    r = tasa / 4;
  else if (freq === 'diario')     r = tasa / n;
  else                            r = tasa;

  const rnd = (v) => Math.round(v * 100) / 100;
  let saldo = capital;
  let fecha = addFreqPeriod(new Date(fecha_inicio + 'T00:00:00'), freq);
  const cuotas = [];

  for (let k = 1; k <= n; k++) {
    let cap, inte;
    const m = String(metodo).toLowerCase();
    if (m === 'insoluto') {
      cap  = capital / n;
      inte = saldo * r;
    } else {
      inte = capital * r;
      cap  = capital / n;
    }
    if (k === n) cap = saldo;
    cap  = rnd(cap);
    inte = rnd(inte);
    cuotas.push({ k, fecha: new Date(fecha), capital: cap, interes: inte, cuota: rnd(cap + inte), capitalPagado: 0, interesPagado: 0, estado: 'pendiente' });
    saldo = rnd(Math.max(0, saldo - cap));
    fecha = addFreqPeriod(fecha, freq);
  }

  // Distribute balance_pendiente — mirror backend logic (interest first, then capital)
  const totalPrestamo = rnd(cuotas.reduce((s, c) => s + c.capital + c.interes, 0));
  let pagado = rnd(Math.max(0, totalPrestamo - Math.min(Number(balance_pendiente) || totalPrestamo, totalPrestamo)));

  for (const c of cuotas) {
    if (pagado <= 0) break;
    if (rnd(pagado) >= rnd(c.capital + c.interes)) {
      c.capitalPagado = c.capital; c.interesPagado = c.interes; c.estado = 'pagada';
      pagado = rnd(pagado - (c.capital + c.interes));
    } else {
      const intAp = rnd(Math.min(pagado, c.interes));
      c.interesPagado = intAp; pagado = rnd(pagado - intAp);
      const capAp = pagado > 0 ? rnd(Math.min(pagado, c.capital)) : 0;
      c.capitalPagado = capAp; pagado = rnd(pagado - capAp);
      c.estado = 'parcial'; break;
    }
  }
  return cuotas;
}

/* ─── Amortización preview dialog ──────────────────────────────────────────── */
function AmortizacionPreviewDialog({ row, onClose, onImport }) {
  const cuotas = useMemo(() => {
    if (!row) return [];
    const params = extractLoanParams(row);
    if (params.metodo === 'plazo_indefinido') return [];
    return calcAmortizacionPreview(params);
  }, [row]);

  const fmt     = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => d instanceof Date ? d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

  const pagadas   = cuotas.filter(c => c.estado === 'pagada').length;
  const parciales = cuotas.filter(c => c.estado === 'parcial').length;
  const pendientes = cuotas.filter(c => c.estado === 'pendiente').length;

  const params = row ? extractLoanParams(row) : {};

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" fontWeight={800}>Tabla de Amortización</Typography>
            <Typography variant="caption" color="text.secondary">
              Fila #{row?.id} · {cuotas.length} cuotas · Capital {fmt(params.capital)} · Tasa {params.interes}% · {String(params.metodo).toUpperCase()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {pagadas   > 0 && <Chip label={`${pagadas} pagadas`}    size="small" sx={{ bgcolor: GREEN, color: '#fff', fontWeight: 700, fontSize: 11 }} />}
            {parciales > 0 && <Chip label={`${parciales} parcial`}  size="small" color="warning" variant="outlined" sx={{ fontWeight: 700, fontSize: 11 }} />}
            {pendientes > 0 && <Chip label={`${pendientes} pendientes`} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: 11 }} />}
            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}><Close fontSize="small" /></IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider />
      {/* Import action bar */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box>
          {row?._status === STATUS.ok && (
            <Chip icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} label="Préstamo importado exitosamente" sx={{ bgcolor: GREEN, color: '#fff', fontWeight: 700 }} />
          )}
          {row?._status === STATUS.error && (
            <Typography variant="caption" color="error.main" fontWeight={600}>{row._errors?.[0] || 'Error al importar'}</Typography>
          )}
          {(row?._status === STATUS.pending || row?._status === STATUS.importing) && (
            <Typography variant="caption" color="text.secondary">Revisa la tabla y presiona Importar para guardar este préstamo.</Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={row?._status === STATUS.ok || row?._status === STATUS.importing}
          onClick={() => onImport(row, cuotas)}
          startIcon={row?._status === STATUS.importing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : row?._status === STATUS.ok ? <CheckCircle /> : null}
          sx={{
            borderRadius: 2, px: 3, fontWeight: 700, fontSize: 13,
            bgcolor: row?._status === STATUS.error ? 'error.main' : GREEN,
            '&:hover': { bgcolor: row?._status === STATUS.error ? '#b71c1c' : '#1B5E20' },
            '&:disabled': { bgcolor: alpha(GREEN, 0.3), color: '#fff' },
          }}
        >
          {row?._status === STATUS.importing ? 'Importando…'
            : row?._status === STATUS.ok      ? 'Importado'
            : row?._status === STATUS.error   ? 'Reintentar'
            : 'Importar préstamo'}
        </Button>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        {cuotas.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Sin tabla de amortización (plazo indefinido o datos incompletos).</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['#', 'Fecha', 'Cuota', 'Capital', 'Interés', 'Cap. Pagado', 'Int. Pagado', 'Estado'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', bgcolor: '#FAFAFA', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {cuotas.map(c => (
                  <TableRow key={c.k} sx={{ bgcolor: c.estado === 'pagada' ? GREEN_LIGHT : c.estado === 'parcial' ? alpha('#ed6c02', 0.07) : 'inherit' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 40 }}>{c.k}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(c.fecha)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{fmt(c.cuota)}</TableCell>
                    <TableCell>{fmt(c.capital)}</TableCell>
                    <TableCell>{fmt(c.interes)}</TableCell>
                    <TableCell sx={{ color: c.capitalPagado > 0 ? GREEN : 'text.secondary', fontWeight: c.capitalPagado > 0 ? 700 : 400 }}>{fmt(c.capitalPagado)}</TableCell>
                    <TableCell sx={{ color: c.interesPagado > 0 ? GREEN : 'text.secondary', fontWeight: c.interesPagado > 0 ? 700 : 400 }}>{fmt(c.interesPagado)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {c.estado === 'pagada'    && <Chip label="Pagada"    size="small" sx={{ bgcolor: GREEN, color: '#fff', fontWeight: 700, fontSize: 10 }} />}
                      {c.estado === 'parcial'   && <Chip label="Parcial"   size="small" color="warning" variant="outlined" sx={{ fontWeight: 700, fontSize: 10 }} />}
                      {c.estado === 'pendiente' && <Chip label="Pendiente" size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: 10, borderColor: 'divider' }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Sub-components ────────────────────────────────────────────────────────── */
function StepBadge({ number, done, importing: imp }) {
  return (
    <Avatar sx={{ width: 34, height: 34, bgcolor: done || imp ? GREEN : '#E8F5E9', color: done || imp ? '#fff' : GREEN, fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
      {done ? <CheckCircle sx={{ fontSize: 19 }} /> : number}
    </Avatar>
  );
}

function StepCard({ number, title, badge, topRight, done, children }) {
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid', borderColor: done ? GREEN : 'divider', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Top accent bar */}
      <Box sx={{ height: 4, bgcolor: done ? GREEN : 'transparent', borderRadius: '3px 3px 0 0' }} />
      <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <StepBadge number={number} done={done} />
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">{title}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {badge}
            {topRight}
          </Stack>
        </Stack>
        {children}
      </Box>
    </Paper>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <Box sx={{ px: 2.5, py: 1.5, border: '1.5px solid', borderColor: highlight ? 'error.main' : 'divider', borderRadius: 2, textAlign: 'center', minWidth: 88 }}>
      <Typography variant="caption" fontWeight={600} color={highlight ? 'error.main' : 'text.secondary'} sx={{ letterSpacing: 0.8, textTransform: 'uppercase', display: 'block' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={800} color={highlight ? 'error.main' : 'text.primary'} lineHeight={1.2}>{value}</Typography>
    </Box>
  );
}

/* ─── Main component ────────────────────────────────────────────────────────── */
export default function ImportarPrestamosTab() {
  const [productosRaw, setProductosRaw]       = useState([]);
  const [productos, setProductos]             = useState([]);
  const [productoId, setProductoId]           = useState('');
  const [selectedProductData, setSelectedProductData] = useState(null);
  const [rows, setRows]                       = useState([]);
  const [fileName, setFileName]               = useState('');
  const [alertDialog, setAlertDialog]         = useState({ open: false, message: '', severity: 'success' });
  const [previewRow, setPreviewRow]           = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProductosFinancieros({ usuarioId: localStorage.getItem('id'), idcompany: localStorage.getItem('idcompany') })
      .then(data => {
        if (!Array.isArray(data)) { setProductos([]); return; }
        setProductosRaw(data);
        setProductos(data.map(p => ({ id: p.idprodfinanciero, label: JSON.parse(p.generales).nombre })));
      })
      .catch(() => setProductos([]));
  }, []);

  const activeCols = useMemo(() => {
    const garantiaCols = selectedProductData?.generales?.requiereGarantia
      ? buildGarantiaCols(selectedProductData.generales.categoriasGarantia) : [];
    return buildActiveColumns(selectedProductData?.formularioCampos, selectedProductData?.camposInventario, garantiaCols);
  }, [selectedProductData]);

  const handleProductoChange = (id) => {
    setProductoId(id); setRows([]); setFileName('');
    const raw = productosRaw.find(p => p.idprodfinanciero === id);
    if (!raw) { setSelectedProductData(null); return; }
    const generales = JSON.parse(raw.generales);
    const formularioCampos = JSON.parse(raw.formulario || '{"campos":[]}').campos || [];
    const camposInventario = generales.requiereInventario ? JSON.parse(raw.fields || '[]') : [];
    setSelectedProductData({ generales, formularioCampos, camposInventario });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const normalized = jsonData.map((raw, idx) => {
        const row = {};
        activeCols.forEach(col => { const match = Object.keys(raw).find(k => k === col.label); row[col.key] = match !== undefined ? raw[match] : ''; });
        const errors = validateRow(row, activeCols);
        return { id: idx + 1, ...row, _status: errors.length ? STATUS.error : STATUS.pending, _errors: errors };
      });
      setRows(normalized);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleImportSingle = async (row, cuotas) => {
    const idcompany = localStorage.getItem('idcompany');
    const userId    = localStorage.getItem('id');
    const sucursal  = localStorage.getItem('id_sucursal');
    const today     = new Date().toISOString().split('T')[0];

    const requiereInventario = selectedProductData?.generales?.requiereInventario || false;
    const requiereGarantia   = selectedProductData?.generales?.requiereGarantia   || false;
    const formularioCampos   = selectedProductData?.formularioCampos || [];
    const camposInventario   = selectedProductData?.camposInventario || [];

    const campos = {};
    formularioCampos.forEach(c => { campos[c.id] = coerceValue(row[campoKey(c.id)], c.type); });
    const invCampos = {};
    if (requiereInventario) { camposInventario.forEach(c => { invCampos[c.id] = coerceValue(row[invKey(c.id)], c.type); }); }

    const get = (id) => campos[id];
    const nombre         = String(get(1)  || '');
    const identificacion = String(get(2)  || '');
    const monto          = Number(get(CAMPO_MONTO))      || 0;
    const interes        = Number(get(13))               || 0;
    const mora           = Number(get(14))               || 0;
    const frecuencia     = String(get(CAMPO_FRECUENCIA) || '').toLowerCase();
    const plazo          = Number(get(CAMPO_PLAZO))      || 0;
    const fechaSolicitud = row.fecha_solicitud || today;
    const fechaInicio    = row.fecha_inicio    || today;

    const categoriaGarantia = requiereGarantia ? String(row[garKey('categoria')] || '') : null;
    const garantiaCatData   = requiereGarantia ? camposGarantia.find(c => c.value === categoriaGarantia) : null;
    const garantiaObj       = garantiaCatData
      ? Object.fromEntries(garantiaCatData.campos.map(c => [c.id, coerceValue(row[garKey(c.id)], c.type)]))
      : 'no existe';

    const payload = {
      campos,
      inv_campos: invCampos,
      identificacion,
      nombre,
      monto_solicitado: monto,
      monto_inicial: Number(row.monto_inicial) || 0,
      interes,
      mora_porcentaje: mora,
      frecuencia,
      metodo: String(row.metodo || '').toLowerCase(),
      plazo,
      garantia: garantiaObj,
      categoria_garantia: categoriaGarantia,
      fecha_solicitud: `${fechaSolicitud} 00:00:00`,
      fecha_inicio: fechaInicio,
      balance_pendiente: Number(row.balance_pendiente) || 0,
      atraso: Number(row.atraso) || 0,
      cuotas: cuotas.map(c => ({
        k: c.k,
        fecha: c.fecha instanceof Date ? c.fecha.toISOString().split('T')[0] : c.fecha,
        capital: c.capital, interes: c.interes, cuota: c.cuota,
        capitalPagado: c.capitalPagado, interesPagado: c.interesPagado, estado: c.estado,
      })),
    };

    setRows(prev => prev.map(r => r.id === row.id ? { ...r, _status: STATUS.importing } : r));

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/importar-individual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Usuario-ID': userId, 'X-company-ID': idcompany },
        body: JSON.stringify({ idcompany, id_productofinanciero: productoId, sucursal, created_by: userId, prestamo: payload }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, _status: STATUS.ok, _errors: [] } : r));
      } else {
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, _status: STATUS.error, _errors: [data.error || 'Error'] } : r));
      }
    } catch (err) {
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, _status: STATUS.error, _errors: [err.message || 'Error de red'] } : r));
    }
  };

  /* ─── Derived state ── */
  const importing      = rows.some(r => r._status === STATUS.importing);
  const totalRows      = rows.length;
  const pendingCount   = rows.filter(r => r._status === STATUS.pending).length;
  const errorCount     = rows.filter(r => r._status === STATUS.error).length;
  const okCount        = rows.filter(r => r._status === STATUS.ok).length;
  const livePreviewRow = previewRow ? rows.find(r => r.id === previewRow.id) || previewRow : null;
  const requiereInventario = selectedProductData?.generales?.requiereInventario || false;
  const requiereGarantiaUI = selectedProductData?.generales?.requiereGarantia   || false;

  /* ─── Grid columns ── */
  const gridColumns = useMemo(() => {
    const statusCol = {
      field: '_status', headerName: 'Estado', width: 110, sortable: false,
      renderCell: ({ value, row }) => {
        if (value === STATUS.ok)        return <Chip label="Importado" size="small" sx={{ bgcolor: GREEN, color: '#fff', fontWeight: 700, fontSize: 11 }} />;
        if (value === STATUS.importing) return <Chip icon={<HourglassEmpty sx={{ fontSize: '14px !important' }} />} label="Procesando" size="small" variant="outlined" />;
        if (value === STATUS.error)
          return (
            <Tooltip title={row._errors?.join(' · ') || 'Error'} placement="top">
              <Chip icon={<WarningAmber sx={{ fontSize: '14px !important', color: 'error.main !important' }} />} label="Error" size="small" sx={{ borderColor: 'error.main', color: 'error.main', fontWeight: 700, fontSize: 11 }} variant="outlined" />
            </Tooltip>
          );
        return <Chip label="Listo" size="small" sx={{ borderColor: GREEN, color: GREEN, fontWeight: 700, fontSize: 11 }} variant="outlined" />;
      },
    };
    const dataCols = activeCols.map(col => ({ field: col.key, headerName: col.label, flex: 1, minWidth: 110 }));
    return [{ field: 'id', headerName: '#', width: 50 }, ...dataCols, statusCol];
  }, [activeCols]);

  /* ─── Render ───────────────────────────────────────────────────────────────── */
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={800} color="text.primary" gutterBottom align='left'>
          Importar Préstamos
        </Typography>
        <Typography variant="body2" color="text.secondary" align='left'>
          Siga los pasos a continuación para cargar masivamente sus solicitudes al sistema.
        </Typography>
      </Box>

      {/* ── Steps ── */}
      <Grid container spacing={2} sx={{ mb: rows.length ? 4 : 0 }}>
        {/* Step 1 – Producto */}
        <Grid item xs={12} md={4}>
          <StepCard
            number={1}
            title="Selecciona el producto"
            done={!!productoId}
            badge={
              productoId && (
                <Stack direction="row" spacing={0.5}>
                  {requiereInventario && <Chip icon={<Inventory2 sx={{ fontSize: '13px !important' }} />} label="Inventario" size="small" color="info"    variant="outlined" sx={{ height: 22, fontSize: 11 }} />}
                  {requiereGarantiaUI && <Chip icon={<Shield     sx={{ fontSize: '13px !important' }} />} label="Garantía"  size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: 11 }} />}
                </Stack>
              )
            }
          >
            <FormControl size="small" fullWidth>
              <InputLabel>Producto Financiero</InputLabel>
              <Select value={productoId} label="Producto Financiero" onChange={e => handleProductoChange(e.target.value)} disabled={importing}>
                {productos.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
          </StepCard>
        </Grid>

        {/* Step 2 – Plantilla */}
        <Grid item xs={12} md={4}>
          <StepCard number={2} title="Descarga la plantilla" done={false}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => downloadTemplate(activeCols)}
              disabled={!productoId}
              fullWidth
              sx={{ borderRadius: 2, borderColor: productoId ? GREEN : 'divider', color: productoId ? GREEN : 'text.disabled', fontWeight: 600, '&:hover': { borderColor: GREEN, bgcolor: GREEN_LIGHT } }}
            >
              Descargar .xlsx
            </Button>
            {productoId && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                {activeCols.length} columnas · incluye fila de ejemplo
              </Typography>
            )}
          </StepCard>
        </Grid>

        {/* Step 3 – Archivo */}
        <Grid item xs={12} md={4}>
          <StepCard
            number={3}
            title="Carga el archivo"
            done={rows.length > 0}
            topRight={
              rows.length > 0 && (
                <Button size="small" variant="text" onClick={() => fileInputRef.current?.click()} disabled={importing}
                  sx={{ fontSize: 12, color: GREEN, fontWeight: 600, minWidth: 0, p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                  Cambiar archivo
                </Button>
              )
            }
          >
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileUpload} />

            {rows.length === 0 ? (
              <Button
                variant="outlined"
                startIcon={<FileUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={importing || !productoId}
                fullWidth
                sx={{ borderRadius: 2, borderStyle: 'dashed', fontWeight: 600 }}
              >
                Seleccionar archivo
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: GREEN_LIGHT, border: '1.5px solid', borderColor: GREEN_MID }}>
                <Avatar sx={{ bgcolor: '#fff', width: 36, height: 36, border: '1px solid', borderColor: GREEN_MID }}>
                  <Description sx={{ color: GREEN, fontSize: 20 }} />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap color="text.primary">{fileName}</Typography>
                  <Typography variant="caption" color="success.main" fontWeight={500}>
                    {totalRows} filas cargadas correctamente
                  </Typography>
                </Box>
              </Box>
            )}
          </StepCard>
        </Grid>
      </Grid>

      {/* ── Preview panel ── */}
      {rows.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {/* Panel header */}
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ px: 3, py: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TableRows sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700}>Vista previa de la importación</Typography>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <StatBox label="Total"   value={totalRows}    highlight={false} />
              <StatBox label="Válidos" value={pendingCount + okCount} highlight={false} />
              {errorCount > 0 && <StatBox label="Errores" value={errorCount} highlight />}
            </Stack>
          </Stack>

          <Divider />

          {/* Importing indicator */}
          {importing && (
            <LinearProgress sx={{ height: 3, bgcolor: GREEN_LIGHT, '& .MuiLinearProgress-bar': { bgcolor: GREEN } }} />
          )}

          {/* DataGrid */}
          <DataGrid
            rows={rows}
            columns={gridColumns}
            autoHeight
            density="comfortable"
            disableRowSelectionOnClick
            onRowClick={({ row }) => setPreviewRow(row)}
            getRowClassName={({ row }) => {
              if (row._status === STATUS.error) return 'row-error';
              if (row._status === STATUS.ok)    return 'row-ok';
              return '';
            }}
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': { bgcolor: '#FAFAFA', borderBottom: '1.5px solid', borderColor: 'divider' },
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary' },
              '& .row-error': {
                color: 'error.main',
                '& .MuiDataGrid-cell': { color: 'error.main' },
                '&:hover': { bgcolor: alpha('#d32f2f', 0.04) },
              },
              '& .row-ok': {
                bgcolor: GREEN_LIGHT,
                '&:hover': { bgcolor: GREEN_MID },
              },
              '& .MuiDataGrid-row:hover': { bgcolor: '#F9FAFB' },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': { borderColor: 'divider' },
            }}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />

          {/* Warning message */}
          {errorCount > 0 && !importing && (
            <Box sx={{ mx: 3, mb: 2, mt: 1, p: 2, borderLeft: '4px solid', borderColor: 'error.main', borderRadius: '0 8px 8px 0', bgcolor: alpha('#d32f2f', 0.04) }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ErrorIcon color="error" sx={{ fontSize: 18, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  <Typography component="span" variant="body2" color="error.main" fontWeight={700}>Advertencia: </Typography>
                  Se {errorCount === 1 ? 'ha detectado una fila' : `han detectado ${errorCount} filas`} con errores. Los registros marcados en rojo no serán procesados durante la importación masiva. Por favor, revise el formato de los datos.
                </Typography>
              </Stack>
            </Box>
          )}

          <Divider />

          {/* Footer */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {okCount > 0
                ? `${okCount} de ${totalRows} préstamos importados — haz clic en una fila para ver o importar.`
                : 'Haz clic en una fila para revisar su tabla de amortización e importarla.'}
            </Typography>
            <Button variant="text" color="inherit" sx={{ fontWeight: 600, color: 'text.secondary' }} onClick={() => { setRows([]); setFileName(''); }}>
              Limpiar
            </Button>
          </Stack>
        </Paper>
      )}

      <AlertMessageDialog
        open={alertDialog.open}
        message={alertDialog.message}
        severity={alertDialog.severity}
        handleClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
      />

      <AmortizacionPreviewDialog
        row={livePreviewRow}
        onClose={() => setPreviewRow(null)}
        onImport={handleImportSingle}
      />
    </Box>
  );
}
